import type { CalloutAnnotation, AnnotationCoordinate } from './model';
import type { AnnotationScreenPoint, AnnotationScreenProjection } from './textLayout';
import type { ElevatedAnnotationProjection } from './elevation';

const finite = (point: AnnotationScreenPoint | null): point is AnnotationScreenPoint =>
  !!point && Number.isFinite(point.x) && Number.isFinite(point.y);

/** Cartographic elevation: retain ground-plane geographic offset, remove perspective sideways drift. */
export function uprightLabelPoint(
  ground: AnnotationScreenPoint,
  elevated: AnnotationScreenPoint | null,
): AnnotationScreenPoint | null {
  if (!finite(ground) || !finite(elevated)) return null;
  const separation = Math.hypot(elevated.x - ground.x, elevated.y - ground.y);
  if (separation > 100_000) return null;
  return { x: ground.x, y: ground.y - separation };
}
export function calloutLabelPoint(
  annotation: CalloutAnnotation,
  project: (coordinate: AnnotationCoordinate, height?: number) => AnnotationScreenPoint | null,
): AnnotationScreenPoint | null {
  const elevated = project(annotation.textPosition, annotation.labelHeightAboveMapM ?? 0);
  return annotation.keepLeaderVerticalOnScreen
    ? uprightLabelPoint(project(annotation.textPosition, 0)!, elevated)
    : finite(elevated)
      ? elevated
      : null;
}
/** Invert the same displayed placement for label drag; never write camera-derived coordinates on move. */
export function unprojectUpright(
  point: AnnotationScreenPoint,
  height: number,
  projection: ElevatedAnnotationProjection,
): AnnotationCoordinate | null {
  let coordinate = projection.unprojectAtHeight(point, height) ?? projection.unproject(point);
  const display = (coordinate: AnnotationCoordinate) =>
    uprightLabelPoint(
      projection.project(coordinate),
      projection.projectAtHeight(coordinate, height),
    );
  for (let i = 0; i < 12; i++) {
    const current = display(coordinate);
    if (!current) return null;
    const dx = point.x - current.x,
      dy = point.y - current.y;
    if (Math.hypot(dx, dy) < 0.02) return coordinate;
    const step = 0.000001;
    const east = display([coordinate[0] + step, coordinate[1]]);
    const north = display([coordinate[0], coordinate[1] + step]);
    if (!east || !north) return null;
    const a = (east.x - current.x) / step,
      b = (north.x - current.x) / step;
    const c = (east.y - current.y) / step,
      d = (north.y - current.y) / step;
    const determinant = a * d - b * c;
    if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-8) return null;
    coordinate = [
      coordinate[0] + (d * dx - b * dy) / determinant,
      coordinate[1] + (a * dy - c * dx) / determinant,
    ];
    if (!coordinate.every(Number.isFinite) || Math.abs(coordinate[1]) > 85.051129) return null;
  }
  return null;
}
/** Each tapered dash is part of one continuous envelope. Widths are logical screen units. */
export function taperedLeaderPolygons(
  start: AnnotationScreenPoint,
  end: AnnotationScreenPoint,
  width: number,
  dashed = false,
  unitScale = 1,
): readonly (readonly AnnotationScreenPoint[])[] {
  const dx = end.x - start.x,
    dy = end.y - start.y,
    length = Math.hypot(dx, dy);
  if (!(length > 0.01) || ![length, width].every(Number.isFinite)) return [];
  const nx = -dy / length,
    ny = dx / length;
  const tip = Math.min(width, Math.max(0.5 * unitScale, width / 6));
  const ring = (a: number, b: number) => {
    const point = (t: number, side: number) => ({
      x: start.x + dx * t + (nx * side * (width + (tip - width) * t)) / 2,
      y: start.y + dy * t + (ny * side * (width + (tip - width) * t)) / 2,
    });
    return [point(a, 1), point(b, 1), point(b, -1), point(a, -1), point(a, 1)];
  };
  if (!dashed) return [ring(0, 1)];
  const period = Math.max(width * 5, length / 128),
    on = period * 0.6;
  return Array.from({ length: Math.ceil(length / period) }, (_, i) =>
    ring((i * period) / length, Math.min(1, (i * period + on) / length)),
  );
}
export function projectedCalloutProxy(
  annotation: CalloutAnnotation,
  projection: AnnotationScreenProjection,
): AnnotationCoordinate | null {
  const elevated = projection as Partial<ElevatedAnnotationProjection>;
  const point = calloutLabelPoint(annotation, (coordinate, height = 0) =>
    height && elevated.projectAtHeight
      ? elevated.projectAtHeight(coordinate, height)
      : projection.project(coordinate),
  );
  if (!point) return null;
  const coordinate = projection.unproject(point),
    check = projection.project(coordinate);
  return coordinate.every(Number.isFinite) &&
    finite(check) &&
    Math.hypot(check.x - point.x, check.y - point.y) < 0.05
    ? coordinate
    : null;
}
